'use client';

import React, { useState } from 'react';
import { VOLUME_2022_2025, MIN_VOLUME_YEAR_STR, MAX_VOLUME_YEAR_STR } from 'src/components/constants';

interface DateRangeSelectProps {
  selectedDate: { gte: string; lte: string };
  setSelectedDate: (date: { gte: string; lte: string }) => void;
  id?: string;
}

const DateRangeSelect = ({
  selectedDate,
  setSelectedDate,
  id = 'pulse-date-range-select',
}: DateRangeSelectProps) => {
  const [showErrors, setShowErrors] = useState({ lte: false, gte: false });

  const validateDates = (gteValue?: string, lteValue?: string) => {
      if (!gteValue || !lteValue) {
          setShowErrors({ lte: false, gte: false });
          return true;
      }
      const isGteValid = parseInt(gteValue) <= parseInt(lteValue);
      const isLteValid = parseInt(lteValue) >= parseInt(gteValue);
      setShowErrors({
          gte: !isGteValid,
          lte: !isLteValid,
      });
      return isGteValid && isLteValid;
  };


  const handleGteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    if (newValue) {
      const isValid = validateDates(newValue, selectedDate.lte);
      if (isValid || !selectedDate.lte) {
        setSelectedDate({ ...selectedDate, gte: newValue });
      }
      validateDates(newValue, selectedDate.lte);
    }
  };

  const handleLteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = event.target.value;
    if (newValue) {
        const isValid = validateDates(selectedDate.gte, newValue);
        if (isValid || !selectedDate.gte) {
           setSelectedDate({ ...selectedDate, lte: newValue });
        }
        validateDates(selectedDate.gte, newValue);
    }
  };

  const gteValue = selectedDate?.gte ?? MIN_VOLUME_YEAR_STR;
  const lteValue = selectedDate?.lte ?? MAX_VOLUME_YEAR_STR;

  // Base classes for select elements, removed focus ring styles
  const selectBaseClasses = "w-full h-9 px-3 text-sm border bg-white rounded-full appearance-none focus:outline-none";
  const selectErrorClasses = "border-red-500";

  return (
    <div
      id={id}
      className="flex gap-2 items-end p-4"
    >
      <div className="flex flex-col items-start gap-1 flex-1">
        <label
          htmlFor={`select-from-for-${id}`}
          className="text-sm font-medium text-neutral-700 px-1"
        >
          From
        </label>
        <select
          id={`select-from-for-${id}`}
          value={gteValue}
          className={`${selectBaseClasses} ${showErrors?.gte ? selectErrorClasses : 'border-neutral-300'}`}
          aria-label="Select start year"
          onChange={handleGteChange}
        >
          {VOLUME_2022_2025?.map((option) => (
            <option
              key={`from-${option.value}`}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <span className="text-neutral-500 pb-1.5">
        -
      </span>

      <div className="flex flex-col items-start gap-1 flex-1">
        <label
         htmlFor={`select-through-for-${id}`}
         className="text-sm font-medium text-neutral-700 px-1"
        >
          Through
        </label>
        <select
          id={`select-through-for-${id}`}
          value={lteValue}
          className={`${selectBaseClasses} ${showErrors?.lte ? selectErrorClasses : 'border-neutral-300'}`}
          aria-label="Select end year"
          onChange={handleLteChange}
        >
          {VOLUME_2022_2025?.map((option) => (
            <option
              key={`through-${option.value}`}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default DateRangeSelect;
